import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { saveAs } from "file-saver";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import useApi from "../../../hooks/useApi";
import { useModal } from "../../../contexts/ModalContext";
import { GoTrash } from "react-icons/go";
import { FiDownload } from "react-icons/fi";
import {
    AiOutlineFilePdf,
    AiOutlineFileImage,
    AiOutlineFileWord,
    AiOutlineFileExcel,
    AiOutlineFileUnknown
} from "react-icons/ai";
import ConfirmDeleteAnexoBM from "./ConfirmDeleteAnexoBM";

const Header = styled.div`
    width: 100%;
    display: flex;
    margin-bottom: 15px;
    align-items: center;
`
const Titulo = styled.h2`
    margin: 0;
    font-size: 16px;
    color: #333;
`
const BotaoAdicionar = styled.button`
    background-color: #2563eb;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    &:hover {
        background-color: #1e40af;
    }
    transition: .2s all ease;
    padding: 10px 12px;
    box-sizing: border-box;
    margin-left: auto;
    &:disabled {
        background-color: #93c5fd;
        cursor: not-allowed;
    }
`
const Container = styled.div`
    width: 100%;
    max-height: 380px;
    border: 1px solid rgba(0, 0, 0, 0.3);
    box-sizing: border-box;
    overflow-y: auto;
`
const Lista = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`
const Linha = styled.li`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 13px;
    color: #333;
    &:nth-child(even) {
        background-color: #fafafa;
    }
    &:hover {
        background-color: #e0f7fa;
    }
`
const NomeArquivo = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`
const IconWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`
const IconBtn = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: .2s ease;
    &:hover {
        background: rgba(0, 0, 0, 0.06);
    }
`
const Lixeira = styled(GoTrash)`
    height: 20px;
    width: 20px;
    color: #e11d48;
`
const DownloadIco = styled(FiDownload)`
    height: 20px;
    width: 20px;
    color: #334155;
`
const Vazio = styled.p`
    margin: 0;
    padding: 24px;
    text-align: center;
    color: #64748b;
    font-size: 14px;
`

function normalizeAnexosPayload(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.anexos)) return data.anexos;
    return [];
}

function nomeAnexo(a) {
    const parts = resolveDownloadParts(a);
    return a.nome || a.filename || (parts && parts.arquivo) || "Arquivo";
}

function resolveDownloadParts(anexo) {
    if (anexo.pasta && (anexo.arquivo || anexo.file)) {
        return { pasta: anexo.pasta, arquivo: anexo.arquivo || anexo.file };
    }
    const path = anexo.caminho || anexo.filepath || anexo.url || "";
    if (!path) return null;
    const segs = path.split("/").filter(Boolean);
    if (segs.length < 2) return null;
    return { pasta: segs[segs.length - 2], arquivo: segs[segs.length - 1] };
}

function getFileIcon(filename) {
    const name = String(filename || "").toLowerCase();
    if (name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg")) {
        return <AiOutlineFileImage size={22} color="#4a90e2" />;
    }
    if (name.endsWith(".pdf")) return <AiOutlineFilePdf size={22} color="#e63946" />;
    if (name.endsWith(".doc") || name.endsWith(".docx")) {
        return <AiOutlineFileWord size={22} color="#2a6db0" />;
    }
    if (name.endsWith(".xls") || name.endsWith(".xlsx")) {
        return <AiOutlineFileExcel size={22} color="#207245" />;
    }
    return <AiOutlineFileUnknown size={22} color="#555" />;
}

const ModalAnexosBM = ({ bm, fecharModal }) => {

    const api = useApi();
    const { isModalOpen, openModal, closeModal } = useModal();
    const fileRef = useRef(null);

    const [ anexos, setAnexos ] = useState([]);
    const [ loading, setLoading ] = useState(false);
    const [ uploading, setUploading ] = useState(false);
    const [ selectedAnexoId, setSelectedAnexoId ] = useState(null);

    const fetchAnexos = async () => {

        if (!bm?.id) return;

        setLoading(true);

        try {

            const { data } = await api.get(
                `/Painel/API/ControleMedicao/GetBMAnexos/index.php?idBM=${bm.id}`
            );
            setAnexos(normalizeAnexosPayload(data));

        } catch (e) {

            console.error(e);
            setAnexos([]);

        } finally {

            setLoading(false);

        }

    }

    useEffect(() => {
        fetchAnexos();
    }, [ bm?.id ]);

    const handleDownload = async (anexo) => {

        const parts = resolveDownloadParts(anexo);
        if (!parts) {
            console.warn("Anexo sem dados para download:", anexo);
            return;
        }

        try {

            const { data } = await api.get(
                `/Painel/API/DownloadFile/index.php?file=${encodeURIComponent(parts.arquivo)}&pasta=${encodeURIComponent(parts.pasta)}`,
                { responseType: "blob" }
            );
            saveAs(data, nomeAnexo(anexo));

        } catch (e) {

            console.error(e);

        }

    }

    const handlePickFiles = () => {
        fileRef.current?.click();
    }

    const handleFileChange = async (e) => {

        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length || !bm?.id) return;

        setUploading(true);

        try {

            const formData = new FormData();
            formData.append("idBM", bm.id);
            files.forEach((file, index) => {
                formData.append(`files[${index}]`, file);
            });

            await api.post("/Painel/API/ControleMedicao/AddBMAnexo/", formData);
            await fetchAnexos();

        } catch (err) {

            console.error(err);

        } finally {

            setUploading(false);

        }

    }

    const handleDeleteClick = (id) => {
        setSelectedAnexoId(id);
        openModal("modalDeleteAnexoBM");
    }

    const handleCancelDelete = () => {
        setSelectedAnexoId(null);
        closeModal();
    }

    const tituloBm =
        bm?.num_medicao != null ? `ANEXOS | Nº BM ${bm.num_medicao}` : "ANEXOS DO BOLETIM";

    return (
        <>
            <Overlay state={true}>
                <Modal header size="lg" close={fecharModal}>
                    <Header>
                        <Titulo>{tituloBm}</Titulo>
                        <BotaoAdicionar
                            type="button"
                            onClick={handlePickFiles}
                            disabled={uploading || !bm?.id}
                        >
                            {uploading ? "ENVIANDO..." : "ADICIONAR ARQUIVOS"}
                        </BotaoAdicionar>
                    </Header>
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />
                    <Container>
                        {loading ? (
                            <Vazio>CARREGANDO...</Vazio>
                        ) : anexos.length === 0 ? (
                            <Vazio>Nenhum anexo cadastrado.</Vazio>
                        ) : (
                            <Lista>
                                {anexos.map((a) => (
                                    <Linha key={a.id}>
                                        {getFileIcon(nomeAnexo(a))}
                                        <NomeArquivo title={nomeAnexo(a)}>{nomeAnexo(a)}</NomeArquivo>
                                        <IconWrap>
                                            <IconBtn
                                                title="Baixar"
                                                onClick={() => handleDownload(a)}
                                            >
                                                <DownloadIco />
                                            </IconBtn>
                                            <IconBtn
                                                title="Excluir"
                                                onClick={() => handleDeleteClick(a.id)}
                                            >
                                                <Lixeira />
                                            </IconBtn>
                                        </IconWrap>
                                    </Linha>
                                ))}
                            </Lista>
                        )}
                    </Container>
                </Modal>
            </Overlay>
            {isModalOpen("modalDeleteAnexoBM") && (
                <ConfirmDeleteAnexoBM
                    id={selectedAnexoId}
                    fecharModal={handleCancelDelete}
                    onDelete={fetchAnexos}
                />
            )}
        </>
    );
}

export default ModalAnexosBM;
